import { LitElement, css, html, nothing } from "lit";
import { customElement, state as litState } from "lit/decorators.js";

export type ImageDialogRequest = {
  src: string;
  alt?: string;
  title?: string;
};

export const IMAGE_DIALOG_REQUEST_EVENT = "jonas-image-request";

/**
 * Ask the app-root image dialog to show an image. Consumers dispatch this
 * bubbling, composed event instead of embedding their own dialog so a single
 * shared viewer can render at the app root. That matters because an embedded
 * `afas-dialog` uses `position: fixed`, and any `contain: layout` ancestor
 * (e.g. the source document) would otherwise become its containing block and
 * mis-center the overlay inside that region instead of the viewport.
 */
export function requestImageDialog(
  source: EventTarget,
  request: ImageDialogRequest,
): void {
  source.dispatchEvent(
    new CustomEvent<ImageDialogRequest>(IMAGE_DIALOG_REQUEST_EVENT, {
      detail: request,
      bubbles: true,
      composed: true,
    }),
  );
}

/**
 * Reusable image viewer built on `afas-dialog`. Shows a single image in a
 * fit-content dialog that can be expanded to fullscreen. Used by the source
 * document (AuthorIt `clickresizeembeddedjpegfiletemplate` images) and by the
 * chat composer (pasted-image previews) so both share one viewer.
 */
@customElement("help-jonas-image-dialog")
export class HelpJonasImageDialog extends LitElement {
  static override styles = css`
    :host {
      display: contents;
    }

    .viewer {
      display: flex;
      overscroll-behavior: contain;
    }

    /* The filename sits in a Help-owned header bar that reuses the dialog
       button strip's background, so the strip and this bar read as one
       continuous header above the image. Rendered only when an explicit title
       is supplied — the source-document image viewer passes none, so it shows
       no header bar at all. */
    .image-header {
      box-sizing: border-box;
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      min-height: 40px;
      max-width: calc(100vw - 64px);
      padding: 6px 16px;
      background: var(--color-background-800);
    }

    .image-header__title {
      min-width: 0;
      margin: 0;
      overflow: hidden;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.4;
      color: var(--color-text-on-background);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* The image centers via auto margins. When it is larger than the viewer the
       auto margins collapse to 0, so it aligns to the top-left and stays fully
       reachable by scrolling instead of being clipped (which centered
       flex/grid alignment would do). */
    .viewer .image {
      display: block;
      margin: auto;
    }

    /* Default: fit the whole image inside the viewport (scaled down as needed)
       so it is fully visible without scrolling. The fit-content dialog hugs the
       result. Mirrors the legacy full-screen image view, which scaled the
       image to the window. */
    .viewer--fit {
      overflow: hidden;
    }

    .viewer--fit .image {
      /* The afas-dialog panel is capped at the viewport minus its 24px offset
         on each side (48px total). Inside that, the fit budget subtracts the
         button strip (40px) plus a small safety margin so the fitted image
         stays fully visible without clipping. */
      max-width: calc(100vw - 64px);
      max-height: calc(100vh - 91px);
      width: auto;
      height: auto;
    }

    /* When the filename header bar (40px) is shown, reserve its height too so
       the image still fits without clipping. */
    .viewer--titled.viewer--fit .image {
      max-height: calc(100vh - 131px);
    }

    /* Maximized: show the image at its original size, filling the fullscreen
       content area. Scroll when the image is larger than that area and center
       it on both axes when it is smaller. */
    .viewer--actual {
      flex: 1 1 auto;
      align-self: stretch;
      min-width: 0;
      min-height: 0;
      width: 100%;
      overflow: auto;
    }

    .viewer--actual .image {
      max-width: none;
      max-height: none;
      width: auto;
      height: auto;
    }
  `;

  @litState()
  private accessor open = false;

  @litState()
  private accessor src = "";

  @litState()
  private accessor alt = "";

  @litState()
  private accessor dialogTitle = "";

  // Tracks the afas-dialog fullscreen ("maximize") state so the image can fit
  // the viewport by default and switch to its original size when maximized.
  @litState()
  private accessor maximized = false;

  show(request: ImageDialogRequest): void {
    const src = request.src?.trim();
    if (!src) return;
    this.src = src;
    this.alt = request.alt?.trim() ?? "";
    // Only an explicit title is shown as a header bar. The source-document
    // image viewer passes none (the image alt is not a caption), so it shows
    // the image with no header bar.
    this.dialogTitle = request.title?.trim() ?? "";
    this.maximized = false;
    this.open = true;
    // afas-dialog has no native Escape handling, so close it ourselves. The
    // capture-phase window listener means Escape closes the image viewer
    // before it can reach (and close) an underlying surface such as the source
    // panel.
    window.addEventListener("keydown", this.handleKeydown, true);
  }

  close(): void {
    this.resetState();
  }

  override disconnectedCallback(): void {
    window.removeEventListener("keydown", this.handleKeydown, true);
    super.disconnectedCallback();
  }

  protected override render() {
    const hasTitle = this.dialogTitle.length > 0;
    const viewerClass = `viewer ${
      this.maximized ? "viewer--actual" : "viewer--fit"
    }${hasTitle ? " viewer--titled" : ""}`;
    return html`
      <afas-dialog
        size="fit-content"
        allow-fullscreen
        disable-movement
        .open=${this.open}
        @dialog-close=${this.handleDialogClose}
        @fullscreen-toggled=${this.handleFullscreenToggled}
      >
        ${this.open && this.src
          ? html`${hasTitle
                ? html`<div class="image-header">
                    <h2 class="image-header__title" title=${this.dialogTitle}>
                      ${this.dialogTitle}
                    </h2>
                  </div>`
                : nothing}
              <div class=${viewerClass}>
                <img
                  class="image"
                  src=${this.src}
                  alt=${this.alt}
                  draggable="false"
                />
              </div>`
          : nothing}
      </afas-dialog>
    `;
  }

  private handleDialogClose(): void {
    if (!this.open) return;
    this.resetState();
    this.dispatchEvent(
      new CustomEvent("image-dialog-close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleFullscreenToggled(event: Event): void {
    const detail = (event as CustomEvent<{ fullscreen?: boolean }>).detail;
    if (typeof detail?.fullscreen !== "boolean") return;
    // afas-dialog emits the PRE-toggle value, so the new (target) state is its
    // inverse: entering fullscreen reports { fullscreen: false } and exiting
    // reports { fullscreen: true }.
    this.maximized = !detail.fullscreen;
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (!this.open || event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    this.dismiss();
  };

  private dismiss(): void {
    // Prefer the afas-dialog's animated hide so Escape matches the close (X)
    // button; hide() fires `dialog-close`, which routes back through
    // handleDialogClose. Fall back to an immediate reset if the element is not
    // available.
    const dialog = this.renderRoot.querySelector("afas-dialog") as
      | (Element & { hide?: () => void })
      | null;
    if (typeof dialog?.hide === "function") {
      dialog.hide();
      return;
    }
    this.handleDialogClose();
  }

  private resetState(): void {
    this.open = false;
    this.src = "";
    this.alt = "";
    this.dialogTitle = "";
    this.maximized = false;
    window.removeEventListener("keydown", this.handleKeydown, true);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-image-dialog": HelpJonasImageDialog;
  }

  interface HTMLElementEventMap {
    "jonas-image-request": CustomEvent<ImageDialogRequest>;
  }
}
